import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Link
} from '@mui/material';
import GoogleSignInButton from '../components/Auth/GoogleSignInButton';
import { useLoginOptions } from '../hooks/useLoginOptions';

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const { loginUser, loginWithGoogle, loading } = useAuth();
  const { loaded: optionsLoaded, googleClientId, passwordLoginEnabled } = useLoginOptions();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || "/"; // Default redirect after login

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent event bubbling
    setError(null);
    
    if (!username || !password) {
      setError("Both username and password are required.");
      return;
    }

    try {
      await loginUser(username, password);
      // Successful login - navigation is handled by AuthContext
      // Don't navigate here to avoid conflicts
    } catch (err) {
      // Extract error message from the error object
      let errorMessage = "Invalid email or password. Please try again.";
      
      if (err.message) {
        // Use the error message from AuthContext
        if (err.message.includes("Invalid credentials") || err.message.includes("Invalid email") || err.message.includes("Login failed")) {
          errorMessage = "Invalid email or password. Please check your credentials and try again.";
        } else {
          errorMessage = err.message;
        }
      }
      
      setError(errorMessage);
      // Clear password field for security
      setPassword('');
    }
  };

  const handleGoogleCredential = async (credential) => {
    setError(null);
    try {
      await loginWithGoogle(credential);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <Box 
      sx={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        height: '100vh', 
        width: '100%',  
        display: 'flex', 
        justifyContent: 'center', 
        // Phones: flex-start + the card's auto margins still centre it, but let
        // the page scroll (soft keyboard) instead of clipping the card's top.
        alignItems: { xs: 'flex-start', sm: 'center' },
        overflowY: { xs: 'auto', sm: 'visible' },
        px: { xs: 2, sm: 0 },
        py: { xs: 2, sm: 0 },
        bgcolor: theme.palette.background.default,
        zIndex: 1300
      }}
    >
      <Paper 
        component="form" 
        onSubmit={handleSubmit}
        elevation={6} 
        sx={{ 
          p: { xs: 3, sm: 4 }, 
          maxWidth: 400, 
          width: { xs: '100%', sm: '90%' },
          my: { xs: 'auto', sm: 0 },
          borderRadius: 2,
          // Phones: 16px inputs stop iOS zooming on focus; taller link tap target.
          [theme.breakpoints.down('sm')]: {
            '& .MuiInputBase-input': { fontSize: 16 },
            '& .MuiLink-root': { display: 'inline-block', py: 1 },
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
            <img 
              src="/gva_logo_v2.png" 
              alt="GVA" 
              style={{ height: '3.5rem', width: 'auto' }}
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center' }}>
            <Typography variant="h5" component="span" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
              Time
            </Typography>
            <Typography variant="h5" component="span" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
              Tracker
            </Typography>
          </Box>
        </Box>
        <Typography variant="subtitle1" align="center" mb={3} color="text.secondary">
          Sign in to your account
        </Typography>

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 2,
              '& .MuiAlert-message': {
                width: '100%'
              }
            }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {!optionsLoaded ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <GoogleSignInButton
              clientId={googleClientId}
              onCredential={handleGoogleCredential}
              disabled={loading}
              showDivider={passwordLoginEnabled}
            />
            {!passwordLoginEnabled && loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            {/* Email + password only while the server allows it (PASSWORD_LOGIN_ENABLED). */}
            {passwordLoginEnabled && (
              <>
                <TextField
                  label="Email"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  type="email"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  autoFocus
                  disabled={loading}
                />
                <TextField
                  label="Password"
                  type="password"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2, py: 1.5, bgcolor: theme.palette.primary.main, color: theme.palette.primary.contrastText, '&:hover': { bgcolor: theme.palette.primary.dark } }}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} sx={{ color: theme.palette.primary.contrastText }} /> : 'Log In'}
                </Button>

                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Link
                    component={RouterLink}
                    to="/forgot_password"
                    variant="body2"
                    >
                    Forgot password?
                  </Link>
                </Box>
              </>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default LoginPage;